import React, { useCallback, useEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { __ } from '@wordpress/i18n';

// components
import AiJobList from '../AiJobList';
import ModalDialog from '../ModalDialog';

// icons
import MagicAiIcon from '../../icons/MagicAiIcon';

// hooks
import useAiJobs from '../../hooks/useAiJobs';

// service
import GenerationService from '../../service/generation-service';

import '../../assets/components/ModalStemSplitter.css';
import '../../assets/components/ModalMoodCover.css';

/**
 * Text to music (Beatoven, `mode: 'prompt'`). Reuses the ModalDialog shell and
 * the existing stemSplitter / moodCover styles.
 */
const ModalGeneratePrompt = ({ visible, projectId, onHide }) => {
    const [prompt, setPrompt] = useState('');

    const { jobs, isFetching, waiting, refresh } = useAiJobs({
        queryKey: ['project', projectId, 'generations'],
        queryFn: () => GenerationService.listGenerationsAsync(projectId),
        enabled: Boolean(visible && projectId),
        select: useCallback((list) => list.filter(g => g.mode === 'prompt'), []),
    });

    const create = useMutation({
        mutationFn: (body) => GenerationService.createGenerationAsync(projectId, body),
        onSuccess: () => {
            setPrompt('');
            refresh();
        },
    });

    useEffect(() => {
        if (!visible) setPrompt('');
    }, [visible]);

    const handleHide = () => {
        setPrompt('');
        onHide();
    };

    const handleSubmit = () => {
        if (!prompt.trim() || create.isPending) return;

        create.mutate({ mode: 'prompt', prompt: prompt.trim() });
    };

    return (
        <ModalDialog
            header={__('Genera musica da testo', 'soundbetween')}
            visible={visible}
            onHide={handleHide}
            onSubmit={handleSubmit}
            width={520}>
            <div className="moodCoverModal">
                <p className="moodCoverModal__hint">
                    {__('Descrivi la musica che vuoi generare: genere, mood, strumenti, tempo. La traccia verrà creata dall’AI.', 'soundbetween')}
                </p>
                <textarea
                    className="stemSplitterModal__note"
                    placeholder={__('Es. colonna sonora malinconica al pianoforte, lenta, con archi in sottofondo…', 'soundbetween')}
                    value={prompt}
                    onChange={e => setPrompt(e.target.value)}
                    rows={3}
                />
                <div className="stemSplitterModal__actions">
                    <button
                        className="stemSplitterModal__submit"
                        disabled={!prompt.trim() || create.isPending}
                        onClick={handleSubmit}>
                        <MagicAiIcon/>
                        {create.isPending ? __('Invio…', 'soundbetween') : __('Genera', 'soundbetween')}
                    </button>
                </div>

                <AiJobList
                    title={__('Generazioni', 'soundbetween')}
                    jobs={jobs}
                    loading={isFetching}
                    waiting={waiting}
                    onRefresh={refresh}
                    emptyLabel={__('Nessuna generazione ancora.', 'soundbetween')}
                    renderLabel={(job) => (job.prompt
                        ? job.prompt.slice(0, 48)
                        : __('Traccia', 'soundbetween'))}
                />
            </div>
        </ModalDialog>
    );
};

export default ModalGeneratePrompt;
